import {
  Box,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  Button,
  Select,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FaTimes } from "react-icons/fa";
import { IoSearch } from "react-icons/io5";

const GOLD = "#33cfff";
const CATS = [
  { id: "new", label: "New" },
  { id: "top", label: "Hot" },
  { id: "favorite", label: "Favorite" },
  { id: "random", label: "Random" },
];

/**
 * GameSearchFilter — 搜尋列 + 分類快篩(金色主題)
 * 純 UI + callback,狀態由 GameSearchContent 持有。
 */
const GameSearchFilter = ({
  searchQuery,
  setSearchQuery,
  filterCategory,
  setFilterCategory,
  onClearFilters,
  gamesInfo,
}) => {
  const { t } = useTranslation();
  const { topGameList } = gamesInfo || {};

  const options = useMemo(() => {
    return (topGameList || []).map((game) => ({
      value: game.gameUid,
      label: game?.[`gameNameTw`] || game?.gameNameEn || "",
    }));
  }, [topGameList]);

  return (
    <Box>
      {/* ── 搜尋列 ── */}
      <Flex gap={2} align="center">
        <InputGroup flex={1}>
          <InputLeftElement pointerEvents="none" h="full" pl={2}>
            <Icon as={IoSearch} color={GOLD} boxSize="18px" />
          </InputLeftElement>
          <Input
            placeholder={t(
              "common.search_games_bigger_than_two_words",
              "搜尋遊戲",
            )}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            pl="42px"
            h="44px"
            borderRadius="full"
            bg="rgba(255,255,255,0.06)"
            border="1px solid"
            borderColor="rgba(51,207,255,0.25)"
            color="white"
            _placeholder={{ color: "whiteAlpha.500" }}
            _hover={{ borderColor: "rgba(51,207,255,0.5)" }}
            _focus={{
              borderColor: GOLD,
              boxShadow: `0 0 0 1px ${GOLD}`,
            }}
          />
        </InputGroup>

        {/* 熱門遊戲下拉 */}
        <Select
          placeholder={t("common.hot_games", "熱門遊戲")}
          w={{ base: "108px", md: "150px" }}
          h="44px"
          borderRadius="full"
          bg="rgba(255,255,255,0.06)"
          border="1px solid"
          borderColor="rgba(51,207,255,0.25)"
          color="white"
          flexShrink={0}
          _hover={{ borderColor: "rgba(51,207,255,0.5)" }}
          _focus={{ borderColor: GOLD, boxShadow: `0 0 0 1px ${GOLD}` }}
          sx={{ "& option": { background: "#0c2f43", color: "white" } }}
          onChange={(e) => {
            const item = options.find((i) => i.value == e.target.value);
            if (item) setSearchQuery(item.label);
          }}
        >
          {options?.map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </Select>

        {/* 清除:有搜尋/篩選才顯示 */}
        {(searchQuery || filterCategory) && (
          <IconButton
            aria-label="Clear"
            icon={<FaTimes />}
            onClick={onClearFilters}
            variant="ghost"
            color="whiteAlpha.700"
            borderRadius="full"
            flexShrink={0}
            _hover={{ color: "white", bg: "whiteAlpha.100" }}
          />
        )}
      </Flex>

      {/* ── 分類快篩(獨立一排,不再被擠掉)── */}
      <HStack
        mt={3}
        spacing={2}
        overflowX="auto"
        pb={1}
        css={{
          "&::-webkit-scrollbar": { display: "none" },
          msOverflowStyle: "none",
          scrollbarWidth: "none",
        }}
      >
        {CATS.map((cat) => {
          const active = filterCategory === cat.id;
          return (
            <Button
              key={cat.id}
              flexShrink={0}
              size="sm"
              h="34px"
              px={5}
              borderRadius="full"
              fontWeight={700}
              onClick={() => setFilterCategory(active ? null : cat.id)}
              bgGradient={
                active ? "linear(to-b, #33cfff, #1477a8)" : "transparent"
              }
              color={active ? "#04212e" : "whiteAlpha.800"}
              border="1px solid"
              borderColor={active ? "transparent" : "rgba(51,207,255,0.35)"}
              boxShadow={active ? "0 2px 10px rgba(51,207,255,0.35)" : "none"}
              _hover={
                active
                  ? { filter: "brightness(1.06)" }
                  : { borderColor: GOLD, color: "white" }
              }
              _active={{ transform: "scale(0.96)" }}
            >
              {t(`common.${cat.label}`, cat.label)}
            </Button>
          );
        })}
      </HStack>
    </Box>
  );
};

export default GameSearchFilter;
